/**
 * Course Registration - Entry Point
 * Import và khởi tạo modules cho form đăng ký khóa học
 */

// ===========================
// IMPORT SHARED MODULES
// ===========================

import * as StateManager from '../shared/state-manager.js';
import * as Utils from '../shared/utils.js';
import * as Validator from '../shared/validator.js';
import * as StorageService from '../shared/storage-service.js';
import * as TrafficTracker from '../shared/traffic-tracker.js';
import * as CustomerService from '../shared/customer-service.js';
import * as CampaignManager from '../shared/campaign-manager.js';
import { ensureBatch, ensureVariations } from '../shared/variation-service.js';

// ===========================
// IMPORT COURSE MODULES
// ===========================

import * as UIManager from './ui-manager.js';
import * as CourseManager from './course-manager.js';
import * as FormHandler from './form-handler.js';
import * as DOMInitializer from './dom-initializer.js';

function needsHydration(course) {
    return course.type === 'variable' && (
        !course.variations ||
        course.variations.length === 0 ||
        course.variations.some(v => !v.description || v.description.trim() === '')
    );
}

// ===========================
// JQUERY READY
// ===========================

(function($) {
    'use strict';

    $(document).ready(async function() {
        console.log('🚀 KTVHVN Course Registration - Initialized');

        // ===========================
        // STEP 1: INITIALIZE DOM CACHE
        // ===========================
        StateManager.initializeCache($);

        // ===========================
        // STEP 2: LOAD DATA FROM TEMPLATE
        // ===========================

        // Load course data
        const coursesDataElement = $('#ktvhvn-courses-data');
        if (coursesDataElement.length) {
            try {
                const coursesData = JSON.parse(coursesDataElement.text());
                StateManager.setCoursesData(coursesData);
            } catch (e) {
                console.error('Cannot parse courses JSON', e);
            }
        }

        // Load recently viewed products data
        const recentlyViewedElement = $('#ktvhvn-recently-viewed-data');
        if (recentlyViewedElement.length) {
            try {
                const recentlyViewedData = JSON.parse(recentlyViewedElement.text());
                StateManager.setRecentlyViewedData(recentlyViewedData);
            } catch (e) {
                console.error('Cannot parse recently viewed JSON', e);
            }
        }

        // ===========================
        // STEP 3: DEPENDENCY INJECTION
        // ===========================

        // UIManager dependencies
        UIManager.injectDependencies({
            addCourseToSelection: CourseManager.addCourseToSelection,
            getCampaignDiscount: CampaignManager.getCampaignDiscount,
            loadPromotionalCampaigns: CampaignManager.loadPromotionalCampaigns
        });

        // CourseManager dependencies
        CourseManager.injectDependencies({
            renderSelectedCourses: UIManager.renderSelectedCourses,
            updateTotals: UIManager.updateTotals,
            refreshDropdown: UIManager.refreshDropdown
        });

        // FormHandler dependencies
        FormHandler.injectDependencies({
            getCampaignDiscount: CampaignManager.getCampaignDiscount,
            renderSelectedCourses: UIManager.renderSelectedCourses,
            updateTotals: UIManager.updateTotals
        });

        // CampaignManager dependencies
        CampaignManager.injectDependencies({
            displayTotalsWithDiscount: UIManager.displayTotalsWithDiscount,
            updateTotals: UIManager.updateTotals
        });

        // DOMInitializer dependencies
        DOMInitializer.injectDependencies({
            addCourseToSelection: CourseManager.addCourseToSelection,
            removeCourse: CourseManager.removeCourse,
            changeVariation: CourseManager.changeVariation,
            handleSubmitRegistration: FormHandler.handleSubmitRegistration,
            handleDirectSubmitRegistration: FormHandler.handleDirectSubmitRegistration, // For "Gửi đăng ký" button - direct submit
            handleDirectSubmitWithPayment: FormHandler.handleDirectSubmitWithPayment,
            handleTrialPayment: FormHandler.handleTrialPayment,
            handlePrintQuote: FormHandler.handlePrintQuote,
            showAllCampaignsPopup: CampaignManager.showAllCampaignsPopup,
            checkCustomerExists: CustomerService.checkCustomerExists,
            applyBestCampaign: CampaignManager.applyBestCampaign
        });

        // ===========================
        // STEP 4: INITIALIZE MODULES
        // ===========================

        // Track traffic source
        TrafficTracker.trackTrafficSource();

        // Prefetch critical free-course variations before any auto-add
        try {
            await ensureBatch([9330, 36697]); // FREE_COURSE_GENERAL, FREE_COURSE_HCSN
        } catch (e) {
            console.warn('Prefetch free courses failed', e);
        }

        // Load saved courses from localStorage
        StorageService.loadCoursesFromStorage();

        // Hydrate variations/descriptions for restored courses before any render
        const hydratedSelected = StateManager.getSelectedCourses();
        const hydrateIds = hydratedSelected
            .filter(needsHydration)
            .map(item => item.product_id);

        if (hydrateIds.length > 0) {
            try {
                const fetchedMap = await ensureBatch(hydrateIds);
                hydratedSelected.forEach(course => {
                    const newVars = fetchedMap[course.product_id];
                    if (newVars && newVars.length) {
                        course.variations = newVars;
                        if (course.selected_variation) {
                            const newSel = newVars.find(v => parseInt(v.variation_id, 10) === parseInt(course.selected_variation.variation_id, 10));
                            if (newSel) {
                                course.selected_variation = newSel;
                                course.final_price = parseFloat(newSel.price) || 0;
                            }
                        }
                    }
                });
                StateManager.setSelectedCourses(hydratedSelected);
            } catch (e) {
                console.warn('Hydrate stored courses failed', e);
            }
        }

        // Load saved customer info
        StorageService.loadCustomerInfo();

        // Auto add course from query string (only if no saved courses)
        if (StateManager.getSelectedCourses().length === 0) {
            const autoAddDataElement = $('#ktvhvn-auto-add-data');
            if (autoAddDataElement.length) {
                try {
                    const autoAddData = JSON.parse(autoAddDataElement.text());
                    if (autoAddData.product_id) {
                        await ensureVariations(parseInt(autoAddData.product_id, 10));
                        await CourseManager.autoAddProductFromURL(autoAddData);
                    }
                } catch (e) {
                    console.warn('Cannot parse auto add payload', e);
                }
            }
        }

        // Auto add recently viewed products (only if no saved courses and no URL auto-add)
        if (StateManager.getSelectedCourses().length === 0 && StateManager.getRecentlyViewedData().length > 0) {
            await CourseManager.autoAddRecentlyViewed();
        }

        // If courses were loaded from storage, render them
        if (StateManager.getSelectedCourses().length > 0) {
            UIManager.renderSelectedCourses();
            UIManager.updateTotals();
        }

        // Initialize custom autocomplete
        UIManager.initializeCustomAutocomplete();

        // ===========================
        // STEP 5: BIND ALL DOM EVENTS
        // ===========================
        DOMInitializer.initializeDOMEvents();

        // Listen to questionnaire price changes (addon)
        jQuery(document).on('ktvhvn_questionnaire_price_changed', function(event, price) {
            window.ktvhvnQuestionnairePrice = parseFloat(price) || 0;
            UIManager.updateTotals();
        });

        console.log('✅ Course Registration initialized successfully!');
    });

})(jQuery);
